import * as React from "react"
import { createPortal } from "react-dom"
import { motion, AnimatePresence } from "motion/react"
import { DollarSign, X, Loader2, Star, AlertCircle, Clock, CheckCircle2, XCircle, History, CreditCard, Banknote } from "lucide-react"
import { useAuth } from "@/contexts/AuthContext"
import coinImg from "@/public/coin.png"

interface RedeemModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

type PaymentMethodType = "cashapp" | "venmo" | "zelle" | "paypal"
type RedemptionStatus = "pending" | "processing" | "completed" | "rejected"

interface PaymentMethod {
  method: PaymentMethodType
  handle: string
  label: string
}

interface Redemption {
  _id: string
  amount: number
  paymentMethod: PaymentMethodType
  paymentHandle: string
  status: RedemptionStatus
  rejectionReason?: string
  createdAt: string
  processedAt?: string
}

function Toast({ message, type, onClose }: { message: string; type: "success" | "error"; onClose: () => void }) {
  React.useEffect(() => {
    const timer = setTimeout(onClose, 4000)
    return () => clearTimeout(timer)
  }, [onClose])

  const isSuccess = type === "success"
  return (
    <motion.div
      initial={{ opacity: 0, y: 50, scale: 0.9 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 50, scale: 0.9 }}
      className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[60]"
    >
      <div className={`flex items-center gap-3 px-6 py-4 border-3 border-[#2A2A45] rounded-xl shadow-[0_0_10px_rgba(243,112,32,0.15)] ${isSuccess ? "bg-[#32CD32]" : "bg-red-500"}`}>
        {isSuccess ? (
          <motion.div animate={{ rotate: [0, 360] }} transition={{ duration: 0.5 }}>
            <Star className="w-5 h-5 text-yellow-300 fill-yellow-300" />
          </motion.div>
        ) : (
          <AlertCircle className="w-5 h-5 text-white" />
        )}
        <span className="font-bold text-white">{message}</span>
      </div>
    </motion.div>
  )
}

const statusConfig: Record<RedemptionStatus, {
  icon: typeof Clock
  label: string
  bg: string
  border: string
  text: string
  iconColor: string
}> = {
  pending: {
    icon: Clock,
    label: "Pending",
    bg: "bg-amber-100",
    border: "border-amber-400",
    text: "text-amber-700",
    iconColor: "text-amber-500",
  },
  processing: {
    icon: Loader2,
    label: "Processing",
    bg: "bg-blue-100",
    border: "border-blue-400",
    text: "text-blue-700",
    iconColor: "text-blue-500",
  },
  completed: {
    icon: CheckCircle2,
    label: "Completed",
    bg: "bg-green-100",
    border: "border-green-400",
    text: "text-green-700",
    iconColor: "text-green-500",
  },
  rejected: {
    icon: XCircle,
    label: "Rejected",
    bg: "bg-red-100",
    border: "border-red-400",
    text: "text-red-700",
    iconColor: "text-red-500",
  },
}

const paymentMethodIcons: Record<PaymentMethodType, string> = {
  cashapp: "$",
  venmo: "V",
  zelle: "Z",
  paypal: "P",
}

const paymentMethodColors: Record<PaymentMethodType, string> = {
  cashapp: "bg-[#00D632]",
  venmo: "bg-[#008CFF]",
  zelle: "bg-[#6D1ED4]",
  paypal: "bg-[#003087]",
}

export function RedeemModal({ open, onOpenChange }: RedeemModalProps) {
  const { user, getPaymentMethods, createRedemption, getUserRedemptions } = useAuth()
  const [activeTab, setActiveTab] = React.useState<"redeem" | "history">("redeem")
  const [isRedeeming, setIsRedeeming] = React.useState(false)
  const [toast, setToast] = React.useState<{ message: string; type: "success" | "error" } | null>(null)

  // Payment methods state
  const [paymentMethods, setPaymentMethods] = React.useState<PaymentMethod[]>([])
  const [selectedMethod, setSelectedMethod] = React.useState<PaymentMethod | null>(null)
  const [isLoadingMethods, setIsLoadingMethods] = React.useState(false)
  const [methodsLoaded, setMethodsLoaded] = React.useState(false)

  // Amount state
  const [amount, setAmount] = React.useState("")
  const [useFullBalance, setUseFullBalance] = React.useState(true)

  // History state
  const [redemptions, setRedemptions] = React.useState<Redemption[]>([])
  const [isLoadingHistory, setIsLoadingHistory] = React.useState(false)
  const [historyLoaded, setHistoryLoaded] = React.useState(false)

  const availableBalance = user?.balance?.available ?? 0
  const pendingBalance = user?.balance?.pending ?? 0
  const currentPoints = user?.points?.current ?? 0

  const effectiveAmount = useFullBalance ? availableBalance : parseFloat(amount) || 0
  const isValidAmount = effectiveAmount > 0 && effectiveAmount <= availableBalance

  // Fetch payment methods when modal opens
  React.useEffect(() => {
    if (!open || methodsLoaded) return

    const fetchMethods = async () => {
      setIsLoadingMethods(true)
      try {
        const result = await getPaymentMethods()
        if (result.success && result.methods) {
          setPaymentMethods(result.methods)
          if (result.methods.length > 0) {
            setSelectedMethod(result.methods[0])
          }
        }
      } catch {
        // silently fail
      } finally {
        setIsLoadingMethods(false)
        setMethodsLoaded(true)
      }
    }
    fetchMethods()
  }, [open, methodsLoaded, getPaymentMethods])

  // Fetch history when switching to history tab
  React.useEffect(() => {
    if (activeTab !== "history" || !open) return
    if (historyLoaded) return

    const fetchHistory = async () => {
      setIsLoadingHistory(true)
      try {
        const result = await getUserRedemptions()
        if (result.success && result.redemptions) {
          setRedemptions(result.redemptions)
        }
      } catch {
        // silently fail
      } finally {
        setIsLoadingHistory(false)
        setHistoryLoaded(true)
      }
    }
    fetchHistory()
  }, [activeTab, open, historyLoaded, getUserRedemptions])

  // Reset state when modal closes
  React.useEffect(() => {
    if (!open) {
      setActiveTab("redeem")
      setHistoryLoaded(false)
      setMethodsLoaded(false)
      setAmount("")
      setUseFullBalance(true)
      setSelectedMethod(null)
    }
  }, [open])

  const handleRedeem = async () => {
    if (isRedeeming || !isValidAmount || !selectedMethod) return
    setIsRedeeming(true)
    try {
      const result = await createRedemption(
        effectiveAmount,
        selectedMethod.method,
        selectedMethod.handle
      )
      if (result.success) {
        setHistoryLoaded(false) // force refetch on next tab switch
        setToast({ message: "Redemption submitted!", type: "success" })
        onOpenChange(false)
      } else {
        setToast({ message: result.message || "Redemption failed", type: "error" })
      }
    } catch {
      setToast({ message: "Could not process redemption. Try again later.", type: "error" })
    } finally {
      setIsRedeeming(false)
    }
  }

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    // Only allow numbers and one decimal point with up to 2 decimal places
    if (/^\d*\.?\d{0,2}$/.test(value) || value === "") {
      setAmount(value)
      setUseFullBalance(false)
    }
  }

  const handleUseFullBalance = () => {
    setUseFullBalance(true)
    setAmount("")
  }

  return createPortal(
    <>
      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-[100] flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            {/* Backdrop */}
            <motion.div
              className="absolute inset-0 bg-black/50 backdrop-blur-sm"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => !isRedeeming && onOpenChange(false)}
            />

            {/* Modal Card */}
            <motion.div
              className="relative bg-[#1C1C32] border-4 border-[#2A2A45] rounded-2xl shadow-[8px_8px_0_0_#1a1a1a] w-full max-w-md overflow-hidden"
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ type: "spring", bounce: 0.3 }}
            >
              {/* Header */}
              <div className="bg-gradient-to-r from-[#FFD700] via-[#FFA500] to-[#FF6B00] p-5 relative overflow-hidden">
                <motion.div
                  className="absolute inset-0 opacity-20 pointer-events-none"
                  style={{
                    backgroundImage: "repeating-linear-gradient(45deg, transparent, transparent 10px, white 10px, white 20px)",
                  }}
                  animate={{ x: [0, 40] }}
                  transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                />
                <motion.button
                  type="button"
                  onClick={() => !isRedeeming && onOpenChange(false)}
                  className="absolute top-3 right-3 z-20 w-8 h-8 rounded-full bg-black/20 flex items-center justify-center text-white hover:bg-black/40 transition-colors cursor-pointer"
                >
                  <X className="w-4 h-4" />
                </motion.button>
                <div className="relative pr-12 flex items-center gap-3">
                  <motion.div
                    className="w-12 h-12 rounded-xl bg-[#1C1C32]/40 flex items-center justify-center"
                    animate={{ rotate: [0, 5, -5, 0] }}
                    transition={{ duration: 3, repeat: Infinity }}
                  >
                    <DollarSign className="w-6 h-6 text-white" />
                  </motion.div>
                  <div>
                    <h3 className="text-xl font-black text-white" style={{ textShadow: "2px 2px 0 rgba(0,0,0,0.2)" }}>
                      Redeem Earnings
                    </h3>
                    <p className="text-white/80 text-sm font-medium">Cash out your available balance</p>
                  </div>
                </div>
              </div>

              {/* Tab Switcher */}
              <div className="flex bg-[#1A1A30] border-b-3 border-[#2A2A45]">
                <button
                  onClick={() => setActiveTab("redeem")}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 font-bold text-sm transition-all cursor-pointer ${
                    activeTab === "redeem"
                      ? "bg-[#1C1C32] text-[#F37020] border-b-3 border-[#F37020] -mb-[3px]"
                      : "text-[#6A6A8A] hover:text-[#9A9ABF]"
                  }`}
                >
                  <DollarSign className="w-4 h-4" />
                  Redeem
                </button>
                <button
                  onClick={() => setActiveTab("history")}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 font-bold text-sm transition-all cursor-pointer ${
                    activeTab === "history"
                      ? "bg-[#1C1C32] text-[#F37020] border-b-3 border-[#F37020] -mb-[3px]"
                      : "text-[#6A6A8A] hover:text-[#9A9ABF]"
                  }`}
                >
                  <History className="w-4 h-4" />
                  History
                </button>
              </div>

              {/* Body — Redeem Tab */}
              {activeTab === "redeem" && (
                <motion.div
                  className="p-6 space-y-5"
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.15 }}
                >
                  {isLoadingMethods ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="w-7 h-7 animate-spin text-[#6A6A8A]" />
                    </div>
                  ) : (
                    <>
                      {/* Balance display */}
                      <div className="bg-gradient-to-br from-green-50 to-emerald-50 border-3 border-[#2A2A45] rounded-xl p-4 shadow-[0_0_8px_rgba(243,112,32,0.12)]">
                        <p className="text-sm font-bold text-[#6A6A8A] mb-1">Available Balance</p>
                        <motion.p
                          className="text-4xl font-black text-[#32CD32]"
                          animate={{ scale: [1, 1.02, 1] }}
                          transition={{ duration: 2, repeat: Infinity }}
                        >
                          ${availableBalance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </motion.p>
                      </div>

                      {/* Amount Selection */}
                      {availableBalance > 0 && (
                        <div className="space-y-3">
                          <p className="text-sm font-bold text-[#9A9ABF] flex items-center gap-2">
                            <Banknote className="w-4 h-4" />
                            Amount to Redeem
                          </p>
                          <div className="flex gap-2">
                            <button
                              onClick={handleUseFullBalance}
                              className={`flex-1 px-3 py-2.5 rounded-xl font-bold text-sm border-2 transition-all cursor-pointer ${
                                useFullBalance
                                  ? "bg-[#F37020] border-[#F37020] text-white"
                                  : "bg-[#1C1C32] border-[#2A2A45] text-[#9A9ABF] hover:border-[#F37020]/50"
                              }`}
                            >
                              Full Balance
                            </button>
                            <div className="flex-1 relative">
                              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6A6A8A] font-bold">$</span>
                              <input
                                type="text"
                                inputMode="decimal"
                                placeholder="0.00"
                                value={amount}
                                onChange={handleAmountChange}
                                className={`w-full pl-7 pr-3 py-2.5 rounded-xl font-bold text-sm border-2 bg-[#1C1C32] text-white placeholder-[#6A6A8A] outline-none transition-all ${
                                  !useFullBalance
                                    ? "border-[#F37020]"
                                    : "border-[#2A2A45] focus:border-[#F37020]"
                                }`}
                              />
                            </div>
                          </div>
                          {!useFullBalance && parseFloat(amount) > availableBalance && (
                            <p className="text-xs text-red-400 flex items-center gap-1">
                              <AlertCircle className="w-3 h-3" />
                              Amount exceeds available balance
                            </p>
                          )}
                        </div>
                      )}

                      {/* Payment Method Selection */}
                      {paymentMethods.length > 0 && availableBalance > 0 && (
                        <div className="space-y-3">
                          <p className="text-sm font-bold text-[#9A9ABF] flex items-center gap-2">
                            <CreditCard className="w-4 h-4" />
                            Payment Method
                          </p>
                          <div className="grid grid-cols-2 gap-2">
                            {paymentMethods.map((method) => (
                              <button
                                key={method.method}
                                onClick={() => setSelectedMethod(method)}
                                className={`p-3 rounded-xl border-2 transition-all cursor-pointer text-left ${
                                  selectedMethod?.method === method.method
                                    ? "border-[#F37020] bg-[#F37020]/10"
                                    : "border-[#2A2A45] bg-[#1C1C32] hover:border-[#F37020]/50"
                                }`}
                              >
                                <div className="flex items-center gap-2 mb-1">
                                  <div className={`w-6 h-6 rounded-md ${paymentMethodColors[method.method]} flex items-center justify-center text-white text-xs font-black`}>
                                    {paymentMethodIcons[method.method]}
                                  </div>
                                  <span className="font-bold text-white text-sm">{method.label}</span>
                                </div>
                                <p className="text-xs text-[#6A6A8A] truncate">{method.handle}</p>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      {paymentMethods.length === 0 && availableBalance > 0 && (
                        <div className="flex items-start gap-2 p-3 bg-amber-50 border-2 border-amber-300 rounded-xl">
                          <AlertCircle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                          <p className="text-xs text-amber-700 font-medium">
                            No payment methods on file. Please update your payment information in your profile settings.
                          </p>
                        </div>
                      )}

                      {/* Pending Balance */}
                      {pendingBalance > 0 && (
                        <div className="bg-[#1C1C32] border-2 border-[#2A2A45]/40 rounded-xl p-4">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <Clock className="w-4 h-4 text-amber-400" />
                              <p className="text-sm font-bold text-[#9A9ABF]">Pending Balance</p>
                            </div>
                            <motion.p
                              className="text-xl font-black text-amber-400"
                              animate={{ opacity: [0.8, 1, 0.8] }}
                              transition={{ duration: 2, repeat: Infinity }}
                            >
                              ${pendingBalance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </motion.p>
                          </div>
                          <p className="text-[11px] text-[#6A6A8A] leading-relaxed">
                            Pending balance comes from raffle winnings, bonuses, and rewards. It moves to your available balance once approved.
                          </p>
                        </div>
                      )}

                      {/* Points display */}
                      <div className="flex items-center gap-3 bg-[#1C1C32] border-2 border-[#2A2A45]/40 rounded-xl p-3">
                        <motion.img
                          src={coinImg}
                          alt=""
                          className="w-7 h-7"
                          animate={{ rotate: [0, 360] }}
                          transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
                        />
                        <div>
                          <p className="text-xs text-[#6A6A8A] font-medium">Current Points</p>
                          <p className="text-lg font-black text-white">{currentPoints.toLocaleString()}</p>
                        </div>
                      </div>

                      {availableBalance <= 0 && (
                        <div className="flex items-start gap-2 p-3 bg-amber-50 border-2 border-amber-300 rounded-xl">
                          <AlertCircle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                          <p className="text-xs text-amber-700 font-medium">
                            No available balance to redeem. Keep submitting deals to earn more!
                          </p>
                        </div>
                      )}

                      {/* Summary */}
                      {isValidAmount && selectedMethod && (
                        <div className="bg-[#1A1A30] border-2 border-[#2A2A45] rounded-xl p-4">
                          <p className="text-xs text-[#6A6A8A] font-medium mb-2">Redemption Summary</p>
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-[#9A9ABF]">Amount</span>
                            <span className="font-black text-[#32CD32]">${effectiveAmount.toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between items-center mt-1">
                            <span className="text-sm text-[#9A9ABF]">To</span>
                            <span className="font-bold text-white">{selectedMethod.label} ({selectedMethod.handle})</span>
                          </div>
                        </div>
                      )}

                      {/* Buttons */}
                      <div className="flex gap-3">
                        <motion.button
                          onClick={() => !isRedeeming && onOpenChange(false)}
                          className="flex-1 px-4 py-3 bg-[#1C1C32] border-3 border-[#2A2A45] rounded-xl font-bold text-[#9A9ABF] shadow-[0_0_8px_rgba(243,112,32,0.12)] cursor-pointer"
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.97, boxShadow: "1px 1px 0 0 #1a1a1a", x: 2, y: 2 }}
                        >
                          Cancel
                        </motion.button>
                        <motion.button
                          onClick={handleRedeem}
                          disabled={isRedeeming || !isValidAmount || !selectedMethod}
                          className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 border-3 border-[#2A2A45] rounded-xl font-bold text-lg cursor-pointer ${
                            isValidAmount && selectedMethod
                              ? "bg-[#32CD32] text-white shadow-[0_0_8px_rgba(243,112,32,0.12)]"
                              : "bg-[#222240] text-[#6A6A8A] cursor-not-allowed shadow-[0_0_8px_rgba(243,112,32,0.12)]"
                          }`}
                          whileHover={isValidAmount && selectedMethod ? { scale: 1.02, boxShadow: "5px 5px 0 0 #1a1a1a" } : {}}
                          whileTap={isValidAmount && selectedMethod ? { scale: 0.97, boxShadow: "1px 1px 0 0 #1a1a1a", x: 2, y: 2 } : {}}
                        >
                          {isRedeeming ? (
                            <>
                              <Loader2 className="w-5 h-5 animate-spin" />
                              Submitting...
                            </>
                          ) : (
                            <>
                              <DollarSign className="w-5 h-5" />
                              Redeem ${effectiveAmount.toFixed(2)}
                            </>
                          )}
                        </motion.button>
                      </div>
                    </>
                  )}
                </motion.div>
              )}

              {/* Body — History Tab */}
              {activeTab === "history" && (
                <motion.div
                  className="p-5"
                  initial={{ opacity: 0, x: 10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.15 }}
                >
                  {isLoadingHistory ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="w-7 h-7 animate-spin text-[#6A6A8A]" />
                    </div>
                  ) : redemptions.length > 0 ? (
                    <div className="space-y-3 max-h-80 overflow-y-auto pr-1">
                      {redemptions.map((r, i) => {
                        const cfg = statusConfig[r.status]
                        const StatusIcon = cfg.icon
                        const date = new Date(r.createdAt)
                        return (
                          <motion.div
                            key={r._id}
                            className="p-3 rounded-xl border-2 border-[#2A2A45] bg-[#1C1C32]"
                            initial={{ opacity: 0, x: -15 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: i * 0.04, type: "spring" }}
                          >
                            <div className="flex items-center gap-3">
                              <div className={`w-9 h-9 rounded-lg ${cfg.bg} border-2 ${cfg.border} flex items-center justify-center shrink-0`}>
                                <StatusIcon className={`w-4.5 h-4.5 ${cfg.iconColor} ${r.status === "processing" ? "animate-spin" : ""}`} />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <p className="font-bold text-white text-sm">
                                    ${r.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </p>
                                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${cfg.bg} ${cfg.text} border ${cfg.border}`}>
                                    {cfg.label}
                                  </span>
                                </div>
                                <div className="flex items-center gap-2 mt-0.5">
                                  <div className={`w-4 h-4 rounded ${paymentMethodColors[r.paymentMethod]} flex items-center justify-center text-white text-[10px] font-black`}>
                                    {paymentMethodIcons[r.paymentMethod]}
                                  </div>
                                  <p className="text-[11px] text-[#6A6A8A] truncate">{r.paymentHandle}</p>
                                </div>
                              </div>
                              <p className="text-[11px] text-[#6A6A8A] shrink-0">
                                {date.toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                              </p>
                            </div>
                            {r.status === "rejected" && r.rejectionReason && (
                              <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded-lg">
                                <p className="text-[11px] text-red-600">{r.rejectionReason}</p>
                              </div>
                            )}
                          </motion.div>
                        )
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-10">
                      <motion.div
                        className="w-14 h-14 mx-auto mb-3 rounded-2xl bg-[#1A1A30] flex items-center justify-center"
                        animate={{ y: [0, -5, 0] }}
                        transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}
                      >
                        <History className="w-7 h-7 text-[#6A6A8A]" />
                      </motion.div>
                      <p className="text-[#6A6A8A] font-medium text-sm">No redemptions yet</p>
                      <p className="text-[#6A6A8A] text-xs mt-1">Your redemption history will appear here</p>
                    </div>
                  )}
                </motion.div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Toast */}
      <AnimatePresence>
        {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      </AnimatePresence>
    </>,
    document.body,
  )
}
